import { useState, useEffect } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { ISongInputWrapper } from '../SongForm/SongForm';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import MessageBox from '../../MessageBox/MessageBox';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField/TextField';
import './SongInfo.css';
import { trace } from 'console';

const convertTimeFormatToSeconds = (time: string): number => {
    const [minutes, seconds] = time.split(':').map(Number);
    return minutes * 60 + seconds;
};

const convertSecondsToTimeFormat = (seconds: number): string => {
    if (isNaN(seconds) || seconds < 0) {
        return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export interface ISongInfoInput {
    id: number;
    track?: File;
    trackName?: string;
    title: string;
    lyrics: string;
    author: string;
    length: string;
    lengthInt: number,
    songNumber: string;
}

interface props {
    songNumber: number;
    data: ISongInfoInput;
    setInfo: (newData: ISongInfoInput) => void;
    fetchData: (isMounted: boolean, controller: AbortController) => Promise<void>;
    verses: ISongInputWrapper[];
    choruses: ISongInputWrapper[];
    melodies: ISongInputWrapper[];
}

const LENGTH_REGEX = /^\d{1,2}:\d{2}$/;

const SongInfo = (props: props) => {
    const axiosPrivate = useAxiosPrivate();
    const bandId = sessionStorage.getItem('editedBandId');
    const INFO_URL = bandId ? `/admin/songs/${bandId}` : '/songs';
    const SONG_URL = bandId ? `/admin/songs/${bandId}` : '/songs';
    const MP3_URL = bandId ? `/admin/songsFiles/${bandId}` : '/songsFiles';
    const VERSE_URL = bandId ? `/admin/stanzas/${bandId}` : '/stanzas';
    const CHORUS_URL = bandId ? `/admin/refrains/${bandId}` : '/refrains';
    const MELODY_URL = bandId ? `/admin/melodies/${bandId}` : '/melodies';

    const SUCCESS_MESSAGE = 'Dane zostały poprawnie zapisane!';
    const ERROR_MESSAGE = 'Podczas zapisu danych wystąpił błąd!';
    const ERROR_MESSAGE_FORM = 'Pola wymagane nie mogą być puste!';

    const [open, setOpen] = useState(false);
    const [result, setResult] = useState(true);
    const [fileLength, setFileLength] = useState<string>('');
    const [fileUploaded, setFileUploaded] = useState(false);
    const [existingFileName, setExistingFileName] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState('');

    const [sectionErrors, setSectionErrors] = useState<{
        verses: boolean[];
        choruses: boolean[];
        melodies: boolean[];
    }>({
        verses: [],
        choruses: [],
        melodies: [],
    });

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        formState: {errors},
    } = useForm<ISongInfoInput>({
        defaultValues: props.data,
    });

    useEffect(() => {
         if (props.data) {
            setValue('title', props.data.title);
            setValue('lyrics', props.data.lyrics);
            setValue('author', props.data.author);
            setValue('length', props.data.length);
            const lengthInt = parseInt(props.data.length);
            setValue('lengthInt', lengthInt);
            if (props.data.length.length !== 0) {
                const timeFormat = convertSecondsToTimeFormat(lengthInt);
                setValue('length', timeFormat);
            }
            // if (props.data.title.length !== 0) {
            //     setValue('title', props.data.title);
            // }
            // if (props.data.lyrics.length !== 0) {
            //     setValue('lyrics', props.data.lyrics);
            // }
            // if (props.data.author.length !== 0) {
            //     setValue('author', props.data.author);
            // }
            // if (props.data.length.length !== 0) {
            //     // Wyświetlamy w formacie mm:ss, ale zachowujemy sekundy w props.data
            //     const timeFormat = convertSecondsToTimeFormat(parseInt(props.data.length));
            //     setValue('length', timeFormat);
            //     setValue('lengthInt', parseInt(props.data.length));
            // }
            setValue('id', props.data.id);
            setValue('songNumber', props.data.songNumber);
        }

        // Fetch existing MP3 file info if it exists
        const fetchExistingFile = async () => {
            try {
                const response = await axiosPrivate.get(`${SONG_URL}/${props.data.id}`);
                if (response.data && response.data.plik) {
                    setExistingFileName(response.data.plik);
                    console.log(response.data.plik);
                }
            } catch (error) {
                console.error('Failed to fetch existing file', error);
            }
        };

        fetchExistingFile();
    }, [props.data, props, setValue, axiosPrivate, MP3_URL, getValues]);


    const onSubmit: SubmitHandler<ISongInfoInput> = async (data) => {
        const { track, title, lyrics, author, length, lengthInt, songNumber } = data;
        
        let hasErrors = false;

        if (!title.trim() || !lyrics.trim() || !author.trim() || !length.trim()) {
            setErrorMsg('Wszystkie pola muszą być uzupełnione!');
            setResult(false);
            setOpen(true);
            return;
        }

        let hasChordsInVerse = false;
        let hasChordsInChorus = false;

        const versesErrors = props.verses?.map(verse => {
            if (verse.data.acords.trim()) hasChordsInVerse = true;
            return !verse.data.lyrics.trim();
        }) || [];

        const chorusesErrors = props.choruses?.map(chorus => {
            if (chorus.data.acords.trim()) hasChordsInChorus = true;
            return !chorus.data.lyrics.trim();
        }) || [];

        if (props.verses?.length > 0 && !hasChordsInVerse) {
            setErrorMsg('Przynajmniej jedna zwrotka musi mieć chwyty!');
            hasErrors = true;
        }
        if (props.choruses?.length > 0 && !hasChordsInChorus) {
            setErrorMsg('Przynajmniej jeden refren musi mieć chwyty!');
            hasErrors = true;
        }

        const melodiesErrors = props.melodies?.map(melody => !melody.data.lyrics.trim()) || [];

        setSectionErrors({
            verses: versesErrors,
            choruses: chorusesErrors,
            melodies: melodiesErrors,
        });

        if (hasErrors) {
            setResult(false);
            setOpen(true);
            return;
        }

        try {
            await axiosPrivate.post(
                INFO_URL,
                JSON.stringify({
                    informations: songNumber,
                    title,
                    lyricist: lyrics,
                    musicAuthor: author,
                    songLength: lengthInt.toString(),
                    songLengthInt: lengthInt,
                    songNumber: songNumber,
                }),
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );

            if (track) {
                let mp3FormData = new FormData();
                mp3FormData.append('songFile', track);
                mp3FormData.append('songFileName', track.name);
                mp3FormData.append('songNumber', songNumber.toString());

                await axiosPrivate.post(MP3_URL, mp3FormData);
            }

            const sendSectionData = async (
                type: 'verse' | 'chorus' | 'melody',
                data: ISongInputWrapper[]
            ) => {
                if (!data || data.length === 0) return;
                
                const url =
                    type === 'verse'
                        ? VERSE_URL
                        : type === 'chorus'
                        ? CHORUS_URL
                        : MELODY_URL;

                for (const section of data) {
                    await axiosPrivate.post(
                        url,
                        JSON.stringify({
                            songNumber: props.songNumber,
                            lyrics: section.data.lyrics,
                            acords: section.data.acords,
                            id: section.id,
                        }),
                        {
                            headers: { 'Content-Type': 'application/json' },
                        }
                    );
                }
            };

            await sendSectionData('verse', props.verses);
            await sendSectionData('chorus', props.choruses);
            await sendSectionData('melody', props.melodies);

            setErrorMsg(SUCCESS_MESSAGE);
            setResult(true);

            const isMounted = true;
            const controller = new AbortController();
            props.fetchData(isMounted, controller);
        } catch (error) {
            setErrorMsg(ERROR_MESSAGE);
            setResult(false);
        }

        setOpen(true);
    };

    return (
        <form className="song-info-container" onSubmit={handleSubmit(onSubmit)}>
            <Controller
                name="track"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                    <div className="track-container">
                        <input
                            {...field}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                onChange(file);
                                if (file) {
                                    const reader = new FileReader();
                                    setFileUploaded(true);
                                    reader.onload = function (e) {
                                        const audio = new Audio(e.target?.result as string);
                                        audio.onloadedmetadata = function () {
                                            const duration = audio.duration;
                                            if (!isNaN(duration) && isFinite(duration)) {
                                                const totalSeconds = Math.floor(duration);
                                                const timeFormat = convertSecondsToTimeFormat(totalSeconds);
                                                setValue('length', timeFormat);
                                                setValue('lengthInt', totalSeconds);
                                                props.setInfo({
                                                    ...props.data, 
                                                    length: totalSeconds.toString(),
                                                    track: file,
                                                    lengthInt: totalSeconds
                                                });
                                            } else {
                                                setValue('length', '');
                                            }
                                        };
                                    };

                                    reader.readAsDataURL(file);
                                } else {
                                    setFileUploaded(false);
                                }
                            }}
                            type="file"
                            accept=".mp3"
                            id="track"
                        />
                        <label className="track-input" htmlFor="track">
                            {(value?.name)
                                ? value.name
                                : (existingFileName
                                    ? existingFileName
                                    : 'Załącz MP3')}
                        </label>
                    </div>
                )}
            />
            <div className="song-info-content">
                <Controller
                    name="title"
                    control={control}
                    rules={{required: true}}
                    render={({field}) => (
                        <div>
                            <label>* Tytuł</label>
                            <TextField
                                {...field}
                                value={field.value}
                                onChange={event => {
                                    field.onChange(event.target.value);
                                    props.setInfo({...props.data, title: event.target.value});
                                    setValue('title', event.target.value);
                                }}
                                error={errors.title ? true : false}
                                helperText={errors.title ? 'Tytuł jest wymagany!' : ''}/>
                        </div>
                    )}
                />
                <Controller
                    name="lyrics"
                    control={control}
                    rules={{required: true}}
                    render={({field}) => (
                        <div>
                            <label>* Autor Słów</label>
                            <TextField
                                {...field}
                                value={field.value}
                                onChange={event => {
                                    field.onChange(event.target.value);
                                    props.setInfo({...props.data, lyrics: event.target.value});
                                    setValue('lyrics', event.target.value);
                                }}
                                error={errors.lyrics ? true : false}
                                helperText={errors.lyrics ? 'Autor słów jest wymagany!' : ''}
                            />
                        </div>
                    )}
                />
                <Controller
                    name="author"
                    control={control}
                    rules={{required: true}}
                    render={({field}) => (
                        <div>
                            <label>* Autor Muzyki</label>
                            <TextField
                                {...field}
                                value={field.value}
                                onChange={event => {
                                    field.onChange(event.target.value);
                                    props.setInfo({...props.data, author: event.target.value});
                                    setValue('author', event.target.value);
                                }}
                                error={errors.author ? true : false}
                                helperText={errors.author ? 'Autor muzyki jest wymagany!' : ''}
                            />
                        </div>
                    )}
                />
                <Controller
                    name='length'
                    control={control}
                    rules={{
                        required: getValues('track')?.name ? false : true,
                        validate: (value) => {
                            if (!value) return true;
                            return LENGTH_REGEX.test(value) || 'Nieprawidłowy format czasu';
                        }
                    }}
                    render={({ field }) => (
                        <div>
                            <label>* Długość (mm:ss)</label>
                            <TextField
                                {...field}
                                value={field.value || ""}
                                onChange={event => {
                                    const timeValue = event.target.value;
                                    
                                    // Akceptuj tylko cyfry i dwukropek
                                    if (!/^[\d:]*$/.test(timeValue)) {
                                        return;
                                    }

                                    field.onChange(timeValue);

                                    if (LENGTH_REGEX.test(timeValue)) {
                                        const seconds = convertTimeFormatToSeconds(timeValue);
                                        props.setInfo({
                                            ...props.data, 
                                            length: seconds.toString(),
                                            lengthInt: seconds
                                        });
                                        setValue('lengthInt', seconds);
                                    }
                                }}
                                placeholder="Długość obliczana automatycznie, format: mm:ss"
                                error={errors.length ? true : false}
                                helperText={errors.length ? 'Wprowadź poprawny format czasu (mm:ss)' : ''}
                                inputProps={{
                                    maxLength: 5
                                }}
                            />
                        </div>
                    )}
                />
            </div>
            <Button variant="contained" type="submit" size="large">
                ZAPISZ DANE UTWORU
            </Button>
            <MessageBox open={open} setOpen={setOpen} error={!result} message={errorMsg}/>
        </form>
    );
};

export default SongInfo;
